import { Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Slider, TextField, Typography } from "@mui/material";
import { Control, Controller, UseFormReturn } from "react-hook-form";
import Iconify from "src/components/iconify";
import { ComponentBlock } from "src/sections/mui/component-block";
import { ComponentBlockCustom } from "src/sections/mui/component-block-custom";
import { useBoolean } from "src/hooks/use-boolean";
import { marks, schema, ScrappingFormData } from "./types";
import { useState } from "react";
import { useGetBQCountriesQuery } from "src/context/api/ext/api";


const useScrappingData = () => {
    const { data: countriesData } = useGetBQCountriesQuery();
    const sortedUniqueCountries = Array.from(
        new Map(countriesData?.map((country: any) => [country.country_name, country])).values()
    ).sort((a: any, b: any) => a.country_name.localeCompare(b.country_name));

    const filterOptions = (options: any, { inputValue }: any) => {
        const optionsArray = Array.isArray(options) ? options : [];
        return optionsArray.filter((option: any) =>
            option.country_name.toLowerCase().includes(inputValue.toLowerCase())
        );
    };

    return {
        sortedUniqueCountries,
        filterOptions,
    }

}

export const useScrapping = (handleAddKeywords: (data: ScrappingFormData) => void) => {
    const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean(false)
    const [keywordCount, setKeywordCount]: any = useState([]);
    const [inputValue, setInputValue] = useState('');

    const [titleScrapExist, setTitleScrapExist] = useState('');
    const [descriptionScrapExist, setDescriptionScrapExist] = useState('');

    const { sortedUniqueCountries, filterOptions } = useScrappingData();

    const toggleDevice = (selectedDevice: string, devices: string[]) => {
        const currentIndex = devices.indexOf(selectedDevice);
        const newDevices = [...devices];

        if (currentIndex === -1) {
            newDevices.push(selectedDevice);
        } else {
            newDevices.splice(currentIndex, 1);
        }

        return newDevices;
    };

    return {
        open,
        onClose,
        onOpen,
        keywordCount,
        setKeywordCount,
        inputValue,
        setInputValue,
        sortedUniqueCountries,
        filterOptions,
        toggleDevice,
        titleScrapExist,
        setTitleScrapExist,
        descriptionScrapExist,
        setDescriptionScrapExist,
        handleAddKeywords,
    }
}

interface AddDialogProps extends ReturnType<typeof useScrapping> {
    methods: UseFormReturn<ScrappingFormData>;
}

export default function AddDialog({
    open,
    onClose,
    keywordCount,
    setKeywordCount,
    inputValue,
    setInputValue,
    sortedUniqueCountries,
    filterOptions,
    toggleDevice,
    titleScrapExist,
    setTitleScrapExist,
    descriptionScrapExist,
    setDescriptionScrapExist,
    handleAddKeywords,
    methods: { control, setValue, getValues, watch, formState: { errors }, handleSubmit }
}: AddDialogProps) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Scrap New Keyword(s)</DialogTitle>
            <DialogContent>
                <ComponentBlock sx={{ marginTop: 2 }} title="Keyword Settings">
                    <Controller
                        name="keywords"
                        control={control}
                        render={({ field }: any) => (
                            <Autocomplete
                                multiple
                                freeSolo
                                sx={{ width: '100%', height: 'auto' }}
                                options={watch('keywords')}
                                value={field.value}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                    setKeywordCount(newValue);
                                }}
                                inputValue={inputValue}
                                onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && inputValue.trim() !== '') {
                                        const updatedValue = [...field.value, inputValue.trim()];
                                        setKeywordCount(updatedValue);
                                        event.preventDefault();
                                        field.onChange(updatedValue);
                                        setInputValue('');
                                    }
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputProps={{
                                            ...params.inputProps,
                                            onPaste: async (event: any) => {
                                                let pasteData = null;
                                                try {
                                                    pasteData = await navigator.clipboard.readText();
                                                } catch (err) {
                                                    // Fallback : utiliser event.clipboardData si disponible
                                                    const clipboardData = event.clipboardData?.getData('text');
                                                    if (clipboardData) {
                                                        pasteData = clipboardData;
                                                    }
                                                }
                                                if (pasteData) {
                                                    pasteData = pasteData.split('\n');
                                                    const pasteItems = pasteData.flatMap((item: string) => {
                                                        return item.trim().split(',');
                                                    }).map((item: string) => {
                                                        return item.trim();
                                                    }).filter((item: any) => !!item);

                                                    if (pasteItems.length > 0) {
                                                        setValue('keywords', [...watch('keywords'), ...pasteItems]);
                                                        setKeywordCount([...watch('keywords'), ...pasteItems]);
                                                        setInputValue('');
                                                    }
                                                }
                                            },
                                        }}
                                        variant="outlined"
                                        label="Keyword(s) to scrape"
                                        placeholder="Enter keyword and press Enter"
                                        error={!!errors.keywords}
                                        helperText={errors.keywords ? errors.keywords.message : ''}
                                    />
                                )}
                            />
                        )}
                    />
                </ComponentBlock>

                <ComponentBlock sx={{ marginTop: 5, marginBottom: 1 }} title="Scraping Settings">
                    <Box sx={{ width: '100%' }}>
                        <Controller
                            name="selectedCountry"
                            control={control}
                            render={({ field }: any) => (
                                <Autocomplete
                                    multiple
                                    options={Array.isArray(sortedUniqueCountries) ? sortedUniqueCountries : []}
                                    getOptionLabel={(option: any) => option?.country_name || ''}
                                    filterOptions={filterOptions}
                                    renderOption={(props: any, option: any) => (
                                        <Box component="li" {...props}>
                                            <Iconify icon={`circle-flags:${option?.country_code}`} style={{ marginRight: 8 }} />
                                            {option?.country_name}
                                        </Box>
                                    )}
                                    value={Array.isArray(field.value) ? field.value : []}
                                    onChange={(event, newValue) => field.onChange(newValue || [])}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Scrap search result from"
                                            variant="outlined"
                                            fullWidth
                                            sx={{ mb: 3 }}
                                            error={!!errors.selectedCountry}
                                            helperText={errors.selectedCountry ? errors.selectedCountry.message : ''}
                                        />
                                    )}
                                />
                            )}
                        />

                        <Controller
                            name="platform_scraping"
                            control={control}
                            defaultValue={['Google']} // Default value can be an array
                            render={({ field }) => (
                                <Autocomplete
                                    multiple // Enable multi-select
                                    options={['Google', 'Bing']} // Options for selection
                                    getOptionLabel={(option) => option}
                                    value={field.value || []} // Ensure the value is always an array
                                    onChange={(event, newValue) => field.onChange(newValue)} // Update the form state
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Platform"
                                            variant="outlined"
                                            fullWidth
                                        />
                                    )}
                                />
                            )}
                        />
                    </Box>
                </ComponentBlock>

                {(titleScrapExist !== "" && descriptionScrapExist !== "") && (
                    <Alert sx={{ my: 3, backgroundColor: '#322f33', color: '#e0ac8a' }} severity="error">
                        <Typography sx={{ fontWeight: 900 }}>{titleScrapExist}</Typography>
                        <Typography fontSize={14} sx={{ mt: 0.4 }}>{descriptionScrapExist}</Typography>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()} variant="text" sx={{ mr: 1, color: 'white' }}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSubmit(handleAddKeywords)}>
                    Add keywords ({keywordCount?.length})
                </Button>
            </DialogActions>
        </Dialog>
    )
}